import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, ExternalLink, Bookmark, Building2, IndianRupee, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import JobCardActions from "@/components/JobCardActions";

const TRACK_META = {
  data_cloud: { label: "Data Cloud", classes: "bg-indigo-400/10 text-indigo-300 border-indigo-400/30" },
  sfmc: { label: "SFMC", classes: "bg-teal-400/10 text-teal-300 border-teal-400/30" },
  mc_next: { label: "MC Next", classes: "bg-fuchsia-400/10 text-fuchsia-300 border-fuchsia-400/30" },
};

export default function JobCard({ job, onToggleSave, showMatchScore }) {
  const meta = TRACK_META[job.track] || TRACK_META.data_cloud;

  const handleSaveToggle = async () => {
    try {
      const { data } = await api.post(`/jobs/${job.id}/save`);
      toast.success(data.saved ? "Job saved" : "Removed from saved jobs");
      onToggleSave?.(job.id, data.saved);
    } catch (e) {
      toast.error("Could not update saved jobs");
    }
  };

  const handleApplyClick = async () => {
    try {
      await api.post(`/jobs/${job.id}/track-apply`);
    } catch (e) {
      // best-effort tracking only — never block the actual apply click
    }
  };

  return (
    <motion.div
      className="glass-panel rounded-xl p-5" data-testid={`job-card-${job.id}`}
      whileHover={{ y: -2, boxShadow: "8px 8px 22px rgba(1,4,12,0.45), -4px -4px 16px rgba(255,255,255,0.02), 0 0 0 1px rgba(99,102,241,0.3)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-heading font-semibold text-white">{job.title}</h3>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.classes}`} data-testid={`job-card-track-badge-${job.id}`}>
              {meta.label}
            </span>
            {job.experience_level && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{job.experience_level}</span>
            )}
            {showMatchScore && job.match_score !== undefined && (
              <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-300" data-testid={`job-card-match-score-${job.id}`}>
                <BarChart3 className="w-3 h-3" /> {job.match_score}% match
              </span>
            )}
          </div>
          {job.posted_by_recruiter_id ? (
            <Link to={`/companies/${job.posted_by_recruiter_id}`} data-testid={`job-card-company-link-${job.id}`} className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1 w-fit">
              <Building2 className="w-3.5 h-3.5" /> {job.company}
            </Link>
          ) : (
            <p className="text-sm text-indigo-400">{job.company}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}</span>
            <span className="px-2 py-0.5 rounded-full bg-white/5">{job.job_type}</span>
            {job.salary_range && <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" /> {job.salary_range}</span>}
          </div>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">{job.description}</p>
          {showMatchScore && job.cover_note && (
            <p className="text-xs text-emerald-300/80 mt-2 italic" data-testid={`job-card-cover-note-${job.id}`}>{job.cover_note}</p>
          )}
          <JobCardActions job={job} />
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <button
            onClick={handleSaveToggle} data-testid={`job-save-btn-${job.id}`}
            className={`p-2 rounded-lg transition-colors ${job.is_saved ? "text-amber-400 bg-amber-400/10" : "text-slate-400 hover:text-white bg-white/5"}`}
            title={job.is_saved ? "Remove from saved" : "Save job"}
          >
            <Bookmark className="w-4 h-4" fill={job.is_saved ? "currentColor" : "none"} />
          </button>
          <a
            href={job.apply_url} target="_blank" rel="noopener noreferrer" onClick={handleApplyClick} data-testid={`job-apply-btn-${job.id}`}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Apply <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}
